import React from 'react'
import { Grid, Typography } from '@mui/material'
import i18n from 'simple-react-i18n'
import { startsWith, last, deburr, sumBy } from 'lodash'
import { getHydroSituationColor, getLevelLabel, getTrendIcon } from 'exploitations/utils/AgriUtils'
import { Circle, CircleOutlined } from '@mui/icons-material'
import { getDarkerHex } from 'utils/ColorUtil'
import { ALERT_LEVEL_GROUPS, RISK_OF_SHORTAGE, VP_LEVELS } from 'territory/constants/A3PConstants'
import { hasValue } from 'utils/NumberUtil'
import { DEPTH } from 'utils/PiezometryUtils'

// Construit une fonction de mapping valeur -> coordonnée normalisée `u`, par interpolation
// linéaire par morceaux entre les ancres fournies, avec extrapolation linéaire hors bornes
// (via le 1er / dernier segment). `anchors` = [{ u, value }]. Retourne null si < 2 ancres valides.
const makePiecewiseMapper = (anchors) => {
    const points = anchors
        .filter(({ u, value }) => hasValue(u) && hasValue(value))
        .sort((a, b) => a.value - b.value)
    if (points.length < 2) return null
    return (value) => {
        if (!hasValue(value)) return undefined
        const found = points.findIndex(p => value <= p.value)
        // -1 : au-dessus de la dernière ancre -> dernier segment ; 0 : en-dessous de la première -> premier segment
        const i = found === -1 ? points.length - 1 : found === 0 ? 1 : found
        const a = points[i - 1]
        const b = points[i]
        if (b.value === a.value) return a.u
        return a.u + (value - a.value) / (b.value - a.value) * (b.u - a.u)
    }
}

const closestDryValue = (envelope, targetDate) => {
    if (!envelope?.length) return undefined
    const match = hasValue(targetDate)
        ? envelope.reduce((closest, cur) => (
            cur[0] <= targetDate && (!closest || cur[0] > closest[0]) ? cur : closest
        ), undefined)
        : undefined
    return (match ?? last(envelope))?.[1]
}

const getStationResourceVPLevel = (station) => {
    if (!station || !hasValue(station.lastMeasure)) return undefined
    const targetDate = last(station.measures)?.[0]
    const quinquennial = closestDryValue(station.dryQuinquennial, targetDate)
    const quadrennial = closestDryValue(station.dryQuadrennial, targetDate)
    const triennial = closestDryValue(station.dryTriennial, targetDate)
    if (!hasValue(quinquennial) || !hasValue(quadrennial) || !hasValue(triennial)) return undefined

    const isPiezo = station.dataType === DEPTH
    const isDrierThan = (value, threshold) => isPiezo ? value > threshold : value < threshold

    if (isDrierThan(station.lastMeasure, quinquennial)) return VP_LEVELS.RESTRICTED
    if (isDrierThan(station.lastMeasure, quadrennial)) return VP_LEVELS.NORMAL
    if (isDrierThan(station.lastMeasure, triennial)) return VP_LEVELS.HIGH
    return VP_LEVELS.VERY_HIGH
}

const getResourceVPLevel = (stations) => {
    const levels = (stations || []).map(getStationResourceVPLevel).filter(hasValue)
    return levels.length ? Math.min(...levels) : undefined
}

const getRiskOfShortageLabel = (riskOfShortage) => {
    if (riskOfShortage <= RISK_OF_SHORTAGE.VERY_UNLIKELY) return i18n.veryUnlikely
    if (riskOfShortage <= RISK_OF_SHORTAGE.UNLIKELY) return i18n.unlikely
    if (riskOfShortage <= RISK_OF_SHORTAGE.LIKELY) return i18n.likely
    if (riskOfShortage > RISK_OF_SHORTAGE.LIKELY) return i18n.veryLikely
    return i18n.unknown
}

const getVPLevelLabel = (vpLevel) => {
    switch (vpLevel) {
        case VP_LEVELS.RESTRICTED: return i18n.VPRestricted
        case VP_LEVELS.NORMAL: return i18n.VPNormal
        case VP_LEVELS.HIGH: return i18n.VPHigh
        case VP_LEVELS.VERY_HIGH: return i18n.VPVeryHigh
        default: return i18n.unknown
    }
}

const getSitu = (situ) => {
    if (situ.restrictionColor || (situ.label !== 'Inconnu' && situ.color !== 'grey')) {
        const hexColor = startsWith(situ.color, '#') ? situ.color : getHydroSituationColor(situ.color)
        return (
            <Grid container size={12} spacing={0.5} alignItems={'center'} sx={{ padding: 0.5 }}>
                <Grid container size='auto'>
                    {situ.restrictionColor ?
                        <Circle sx={{ color: situ.restrictionColor }} />
                        :
                        <CircleOutlined />
                    }
                </Grid>
                <Grid
                    container
                    size={'grow'}
                    alignItems='center'
                    justifyContent='center'
                    spacing={1}
                    sx={{
                        borderRadius: 1,
                        backgroundColor: `${hexColor}16`,
                        padding: 0.75,
                        fontSize: '11px',
                    }}
                >
                    {situ.label !== 'Inconnu' &&
                        <Grid container size='auto'>{getTrendIcon(situ.label, situ.color)}</Grid>
                    }
                    <Grid
                        container
                        size='grow'
                        alignItems={'center'}
                        justifyContent={'center'}
                        sx={{ display: { xs: 'none', xl: 'flex' } }}
                    >
                        <Typography color={getDarkerHex(hexColor, 25)}>{getLevelLabel(situ.color)}</Typography>
                    </Grid>
                </Grid>
            </Grid>
        )
    }
    return undefined
}

const getAlertLevelGroup = ({ idAlert, alertLabel }) => {
    if (idAlert < 0) return ALERT_LEVEL_GROUPS.NORMAL
    const label = deburr(alertLabel ?? '').toLowerCase()
    const groups = [ALERT_LEVEL_GROUPS.CRISIS, ALERT_LEVEL_GROUPS.REINFORCED_ALERT, ALERT_LEVEL_GROUPS.ALERT, ALERT_LEVEL_GROUPS.VIGILANCE]
    return groups.find(group => label.includes(group)) ?? ALERT_LEVEL_GROUPS.NORMAL
}

const groupAlertLevels = (alertLevels = []) => [
    { key: ALERT_LEVEL_GROUPS.NORMAL, label: i18n.normal },
    { key: ALERT_LEVEL_GROUPS.VIGILANCE, label: i18n.vigilance },
    { key: ALERT_LEVEL_GROUPS.ALERT, label: i18n.alert },
    { key: ALERT_LEVEL_GROUPS.REINFORCED_ALERT, label: i18n.reinforcedAlert },
    { key: ALERT_LEVEL_GROUPS.CRISIS, label: i18n.crisis },
].map(({ key, label }) => {
    const levels = alertLevels.filter(alertLevel => getAlertLevelGroup(alertLevel) === key)
    return {
        key,
        label,
        nbUg: sumBy(levels, 'nbUg'),
        color: key === ALERT_LEVEL_GROUPS.NORMAL ? undefined : levels.find(({ alertColor }) => alertColor)?.alertColor,
    }
})

export {
    getRiskOfShortageLabel,
    getVPLevelLabel,
    getSitu,
    makePiecewiseMapper,
    getStationResourceVPLevel,
    getResourceVPLevel,
    getAlertLevelGroup,
    groupAlertLevels,
}